import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Download, Search, Upload } from 'lucide-react';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { cn } from '../../../foundation/altzero_base/base/lib/utils';
import { siteService } from '../services/siteService';
import { intervalExportService } from '../services/intervalExportService';
import { NewBadge } from '../components/NewBadge';
import { yesterday } from '../lib/usageGaps';
import { exportMultiSheetXLSX, exportToCSV } from '../utils/exportUtils';
import {
  XLSX_MAX_ROWS, averageDay, averageDaySheetRows, dateRanges, dedupeDays, demandSheetRows, exportSummary,
  intervalRows, monthlyDemand, rangeInvalid, summarySheetRows, type IntervalDayRow,
} from '../lib/intervalExport';
import type { BvSite } from '../types/billValidationTypes';

// NEM12 Interval Export: interval data for one NMI as CSV (one row per
// interval) and XLSX (Summary, Demand profile, Average day, Intervals). The
// data comes from stored intervals (bv_nem_interval) or an uploaded NEM12 file,
// which is parsed on the server and not saved.

type Mode = 'stored' | 'file';

type Loaded = {
  days: IntervalDayRow[];   // one NMI, possibly with provider duplicates
  nmi: string;
  source: string;
  from: string;
  to: string;
};

const startOfMonthsAgo = (day: string, months: number) => {
  const d = new Date(`${day.slice(0, 7)}-01T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.toISOString().slice(0, 10);
};

const num = (n: number | null | undefined, dp = 2) =>
  n == null ? '—' : n.toLocaleString('en-AU', { maximumFractionDigits: dp });

export const IntervalExportPage = () => {
  const lastDay = useMemo(() => yesterday(), []);
  const [mode, setMode] = useState<Mode>('stored');

  // Stored mode
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<BvSite[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [site, setSite] = useState<BvSite | null>(null);
  const [from, setFrom] = useState(() => startOfMonthsAgo(lastDay, 12));
  const [to, setTo] = useState(lastDay);

  // File mode: every NMI in the file, keyed by NMI
  const [fileDays, setFileDays] = useState<Map<string, IntervalDayRow[]> | null>(null);
  const [fileName, setFileName] = useState('');
  const [fileWarnings, setFileWarnings] = useState<string[]>([]);

  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rangeError = rangeInvalid(from, to);

  const switchMode = (m: Mode) => {
    setMode(m);
    setLoaded(null);
    setError(null);
  };

  const search = async () => {
    const nmi = query.trim();
    if (!nmi) return;
    setSearching(true);
    setError(null);
    const res = await siteService.list({ search: nmi, size: 20, sort: 'nmi', sortDir: 'asc' });
    setSearching(false);
    if (!res.success) { setError(res.error || 'Site search failed'); return; }
    const sites = res.data?.sites ?? [];
    const exact = sites.filter((s) => s.nmi?.toUpperCase() === nmi.toUpperCase());
    const pick = sites.length === 1 ? sites[0] : exact.length === 1 ? exact[0] : null;
    setMatches(pick ? null : sites);
    setSite(pick);
    setLoaded(null);
  };

  const loadStored = async () => {
    if (!site || rangeError) return;
    setLoading(true);
    setError(null);
    setLoaded(null);
    try {
      const res = await intervalExportService.getStoredDays(site.id, from, to);
      if (!res.success) { setError(res.error || 'Failed to load interval data'); return; }
      setLoaded({ days: res.data ?? [], nmi: site.nmi ?? '', source: 'Stored interval data', from, to });
    } finally {
      setLoading(false);
    }
  };

  const pickFileNmi = (nmi: string, byNmi: Map<string, IntervalDayRow[]>, name: string) => {
    const days = byNmi.get(nmi) ?? [];
    const dates = days.map((d) => d.date).sort();
    setLoaded({ days, nmi, source: `File ${name}`, from: dates[0] ?? '', to: dates[dates.length - 1] ?? '' });
  };

  const uploadFile = async (file: File) => {
    setLoading(true);
    setError(null);
    setLoaded(null);
    setFileDays(null);
    setFileWarnings([]);
    try {
      const res = await intervalExportService.parseFile(file);
      if (!res.success || !res.data) { setError(res.error || 'Could not read the file'); return; }
      const byNmi = new Map<string, IntervalDayRow[]>();
      for (const d of res.data.days) byNmi.set(d.nmi, [...(byNmi.get(d.nmi) ?? []), d]);
      if (byNmi.size === 0) { setError('The file has no interval data.'); return; }
      setFileDays(byNmi);
      setFileName(res.data.fileName);
      setFileWarnings([...res.data.parseErrors, ...res.data.validationErrors]);
      pickFileNmi([...byNmi.keys()].sort()[0], byNmi, res.data.fileName);
    } finally {
      setLoading(false);
    }
  };

  const report = useMemo(() => {
    if (!loaded) return null;
    const { days, duplicates } = dedupeDays(loaded.days);
    return {
      days,
      summary: exportSummary(days, { nmi: loaded.nmi, source: loaded.source, from: loaded.from, to: loaded.to, duplicates }),
      months: monthlyDemand(days),
      avg: averageDay(days),
    };
  }, [loaded]);

  const fileBase = loaded ? `interval-export-${loaded.nmi}-${loaded.from}-${loaded.to}` : 'interval-export';

  const downloadCsv = () => {
    if (!report) return;
    exportToCSV(intervalRows(report.days).rows, fileBase);
  };

  const downloadXlsx = () => {
    if (!report) return;
    const intervals = intervalRows(report.days, XLSX_MAX_ROWS);
    const note = intervals.truncated
      ? `Intervals sheet holds the first ${intervals.rows.length.toLocaleString('en-AU')} of ${intervals.total.toLocaleString('en-AU')} intervals (Excel row limit). The CSV has them all.`
      : '';
    exportMultiSheetXLSX([
      { name: 'Summary', rows: summarySheetRows(report.summary, note) },
      { name: 'Demand profile', rows: demandSheetRows(report.months) },
      { name: 'Average day (kW)', rows: averageDaySheetRows(report.avg) },
      { name: 'Intervals', rows: intervals.rows },
    ], fileBase);
  };

  const hasData = !!report && report.days.length > 0;

  return (
    <div className="p-6 space-y-4">
      <div>
        <Link to="/bill-validation/report-hub" className="text-xs text-gray-500 hover:underline">← Reports</Link>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-gray-900">NEM12 Interval Export <NewBadge /></h1>
        <p className="text-sm text-gray-500">
          Interval data for one NMI as CSV, or XLSX with a monthly demand profile and average-day curve.
          kW = kWh × 60 ÷ interval length; kVA only where a Q (kVArh) channel exists.
        </p>
      </div>

      <div className="inline-flex rounded-lg border bg-white p-1 text-sm" role="tablist">
        {([['stored', 'Stored data'], ['file', 'Upload NEM12 file']] as const).map(([m, label]) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            data-testid={`interval-export-mode-${m}`}
            className={cn('rounded px-3 py-1.5', mode === m ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50')}
            onClick={() => switchMode(m)}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'stored' ? (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
          <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); void search(); }}>
            <label className="text-xs text-gray-600">
              NMI
              <input
                data-testid="interval-export-nmi"
                className="mt-1 block w-48 rounded border px-2 py-1.5 font-mono text-sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. 4102000000"
              />
            </label>
            <Button type="submit" size="sm" variant="outline" disabled={searching || !query.trim()}>
              <Search className="h-4 w-4" /> {searching ? 'Searching…' : 'Find'}
            </Button>
          </form>
          <label className="text-xs text-gray-600">
            From
            <input type="date" data-testid="interval-export-from" className="mt-1 block rounded border px-2 py-1 text-sm"
              value={from} max={lastDay} onChange={(e) => { setFrom(e.target.value); setLoaded(null); }} />
          </label>
          <label className="text-xs text-gray-600">
            To
            <input type="date" data-testid="interval-export-to" className="mt-1 block rounded border px-2 py-1 text-sm"
              value={to} max={lastDay} onChange={(e) => { setTo(e.target.value); setLoaded(null); }} />
          </label>
          <Button size="sm" data-testid="interval-export-load" disabled={!site || !!rangeError || loading} onClick={() => void loadStored()}>
            {loading ? 'Loading…' : 'Load'}
          </Button>
          {site && (
            <span className="text-xs text-gray-600">
              <span className="font-mono">{site.nmi}</span>
              {' · '}{[site.client?.name, site.addr_street, site.addr_city].filter(Boolean).join(' · ')}
            </span>
          )}
          {rangeError && <span className="w-full text-xs text-red-600">{rangeError}</span>}
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white p-4">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded border px-3 py-1.5 text-sm hover:bg-gray-50">
            <Upload className="h-4 w-4" /> {loading ? 'Reading…' : 'Choose NEM12 file'}
            <input
              type="file"
              data-testid="interval-export-file"
              className="hidden"
              accept=".csv,.txt,.nem12,text/csv,text/plain"
              disabled={loading}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void uploadFile(f); }}
            />
          </label>
          {fileDays && fileDays.size > 1 && (
            <label className="text-xs text-gray-600">
              NMI ({fileDays.size} in file)
              <select
                className="mt-1 block rounded border px-2 py-1 font-mono text-sm"
                value={loaded?.nmi ?? ''}
                onChange={(e) => pickFileNmi(e.target.value, fileDays, fileName)}
              >
                {[...fileDays.keys()].sort().map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          {fileName && <span className="text-xs text-gray-600">{fileName} — not saved</span>}
          {fileWarnings.length > 0 && (
            <details className="w-full text-xs text-amber-800">
              <summary>{fileWarnings.length} parser warning{fileWarnings.length === 1 ? '' : 's'}</summary>
              <ul className="mt-1 list-disc pl-5">{fileWarnings.slice(0, 50).map((w, i) => <li key={i}>{w}</li>)}</ul>
            </details>
          )}
        </div>
      )}

      {mode === 'stored' && matches && (
        <div className="rounded-lg border bg-white p-4 text-sm">
          {matches.length === 0 ? (
            <p className="text-gray-500">No site matches “{query.trim()}”.</p>
          ) : (
            <>
              <p className="mb-2 text-gray-600">{matches.length} sites match — pick one:</p>
              <ul className="space-y-1">
                {matches.map((s) => (
                  <li key={s.id}>
                    <button type="button" className="font-mono text-blue-700 hover:underline"
                      onClick={() => { setMatches(null); setQuery(s.nmi ?? ''); setSite(s); setLoaded(null); }}>
                      {s.nmi || '—'}
                    </button>
                    <span className="ml-2 text-gray-500">{[s.client?.name, s.addr_street, s.addr_city].filter(Boolean).join(' · ')}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {report && !hasData && (
        <p className="text-sm text-gray-500">No interval data for {loaded?.nmi} in this period. Interval data older than the retention window is purged.</p>
      )}

      {report && hasData && (
        <>
          <div className="rounded-lg border bg-white">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
              <div className="text-sm">
                <span className="font-mono text-gray-900">{report.summary.nmi}</span>
                <span className="ml-2 text-xs text-gray-500">{report.summary.source} · {report.summary.periodFrom} to {report.summary.periodTo}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" data-testid="interval-export-csv" onClick={downloadCsv}>
                  <Download className="h-4 w-4" /> CSV
                </Button>
                <Button size="sm" data-testid="interval-export-xlsx" onClick={downloadXlsx}>
                  <Download className="h-4 w-4" /> XLSX
                </Button>
              </div>
            </div>
            <dl data-testid="interval-export-summary" className="grid gap-x-6 gap-y-2 px-4 py-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div><dt className="text-xs text-gray-500">Channels</dt><dd>{report.summary.channels.join(', ')}</dd></div>
              <div><dt className="text-xs text-gray-500">Interval length</dt><dd>{report.summary.intervalLengths.join(', ')} min</dd></div>
              <div><dt className="text-xs text-gray-500">Days with data</dt><dd>{report.summary.daysWithData} / {report.summary.daysExpected}</dd></div>
              <div><dt className="text-xs text-gray-500">Intervals</dt><dd>{report.summary.intervals.toLocaleString('en-AU')}</dd></div>
              <div><dt className="text-xs text-gray-500">Import kWh</dt><dd>{num(report.summary.importKwh)}</dd></div>
              <div><dt className="text-xs text-gray-500">Export kWh</dt><dd>{num(report.summary.exportKwh)}</dd></div>
              <div><dt className="text-xs text-gray-500">Quality (channel-days)</dt>
                <dd>{Object.entries(report.summary.qualityDays).sort().map(([f, n]) => `${f} ${n}`).join(' · ')}</dd></div>
              {report.summary.duplicateDays > 0 && (
                <div><dt className="text-xs text-gray-500">Held by two providers</dt><dd>{report.summary.duplicateDays} channel-days (latest kept)</dd></div>
              )}
              {report.summary.missingDays.length > 0 && (
                <div className="sm:col-span-2 lg:col-span-4">
                  <dt className="text-xs text-gray-500">Missing days ({report.summary.missingDays.length})</dt>
                  <dd className="text-red-700">{dateRanges(report.summary.missingDays)}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="rounded-lg border bg-white">
            <h2 className="border-b px-4 py-3 text-sm font-medium text-gray-900">Demand profile by month</h2>
            <div className="overflow-x-auto">
              <table data-testid="interval-export-demand" className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
                    {['Month', 'Days', 'Import kWh', 'Export kWh', 'Peak kW', 'Peak kW at', 'Peak kVA', 'Peak kVA at', 'Load factor'].map((h) => (
                      <th key={h} className="px-4 py-2 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.months.map((m) => (
                    <tr key={m.month}>
                      <td className="px-4 py-2 font-medium text-gray-900">{m.month}</td>
                      <td className="px-4 py-2">{m.daysWithData}</td>
                      <td className="px-4 py-2">{num(m.importKwh)}</td>
                      <td className="px-4 py-2">{num(m.exportKwh)}</td>
                      <td className="px-4 py-2">{num(m.peakKw)}</td>
                      <td className="px-4 py-2 text-gray-600">{m.peakKwDate ? `${m.peakKwDate} ${m.peakKwTime}` : '—'}</td>
                      <td className="px-4 py-2">{num(m.peakKva)}</td>
                      <td className="px-4 py-2 text-gray-600">{m.peakKvaDate ? `${m.peakKvaDate} ${m.peakKvaTime}` : '—'}</td>
                      <td className="px-4 py-2">{num(m.loadFactor, 3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-lg border bg-white">
            <h2 className="border-b px-4 py-3 text-sm font-medium text-gray-900">
              Average day, import kW per half-hour
            </h2>
            <div className="max-h-96 overflow-auto">
              <table data-testid="interval-export-avg-day" className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b text-left text-gray-500">
                    <th className="px-3 py-1.5 font-medium">Time</th>
                    {report.avg.months.map((m) => <th key={m} className="px-3 py-1.5 font-medium">{m}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.avg.rows.map((r) => (
                    <tr key={r.time}>
                      <td className="px-3 py-1 font-mono text-gray-600">{r.time}</td>
                      {r.kw.map((v, i) => <td key={report.avg.months[i]} className="px-3 py-1">{num(v)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
